import { withI18n } from '@lingui/react'
import { arrayOf, bool, func, shape, string } from 'prop-types'
import { compose } from 'redux'

import { TENANT_AT, TENANT_COM } from 'shared/consts/tenants'
import {
  EXPERIMENT_VARIATION,
  TOP_DEALS_LABEL,
} from 'shared/experiments/consts'
import { getExperiments } from 'shared/experiments/utils/experiments'
import { isEmpty } from 'shared/utils/objectUtils'
import Flag from 'views/components/atoms/Flag'
import { shouldRenderTopDealLabel } from 'views/components/organisms/SliderHorizontal/ProductSliderItem/helpers'
import Experiment from 'views/components/utils/optimizely/Experiment'
import Tenant from 'views/components/utils/Tenant'
import NowLabelWithZipInteractionContainer from 'views/containers/browser/NowLabelWithZipInteractionContainer'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { isProductOutOfTrade } from 'views/utils/productUtils'
import {
  i18nType,
  publicRuntimeConfigType,
  redPointsType,
} from 'views/utils/propTypes'
import { trackingUrls } from 'views/utils/propTypes/adserver'
import { PromotionProductTypeV2 } from 'views/utils/propTypes/api'

import ProductLabelAbo from './ProductLabelAbo'
import ProductLabelCommon from './ProductLabelCommon'
import ProductLabelOutOfTrade from './ProductLabelOutOfTrade'
import ProductLabelPrescription from './ProductLabelPrescription'
import ProductLabelPromotion from './ProductLabelPromotion'
import ProductLabelRedPoint from './ProductLabelRedPoint'

import './ProductLabels.scss'

/**
 * Component renders label only if stock status is not AV.
 * If status is equal AV, only `Out of trade` label will be displayed.
 */
const ProductLabels = ({
  publicRuntimeConfig: {
    pageProperties: { experiments },
  },

  displayAboLabel,
  displayNowWidget,
  handleProductAboLabelClick,
  i18n,
  labels,
  noLinksForPromotion,
  prescription,
  pharmaObl,
  promotion,
  promotionProduct,
  redPointsData,
  stockStatus,
  offerTypeSae,
  trackingUrls,
  upid,
  id,
}) => {
  const productLabelPromotionProps = {
    promotion,
    promotionProduct,
    offerTypeSae,
  }
  if (noLinksForPromotion) {
    productLabelPromotionProps.link = null
    productLabelPromotionProps.dataClientsideHook = null
  }

  /*
   * For tenant IT or FR we get a lot of duplicate labels from Algolia
   * Check SAEFRNT-4402 for more info
   */
  const finalLabels = labels?.length ? [...new Set(labels)] : []

  const topDealExperiment = getExperiments([TOP_DEALS_LABEL], experiments)[0]
  const shouldHaveTopDealLabel = shouldRenderTopDealLabel(topDealExperiment, id)
  return (
    <ul className="a-list-reset a-list-inline o-ProductLabels">
      <ProductLabelPrescription prescription={prescription} />
      {finalLabels.map(label => (
        <ProductLabelCommon label={label} key={`flag-${label}`} />
      ))}
      {/*  Check SAEFRNT-4374 for more details - tbd. deprecation of pharmaObl  */}
      <Tenant tenant={[TENANT_AT, TENANT_COM]}>
        {() =>
          pharmaObl && (
            <ProductLabelCommon
              label={i18n._('product.label.pharmacistobligatory')}
            />
          )
        }
      </Tenant>
      <ProductLabelAbo
        label={i18n._('product.label.availableForAbo')}
        displayAboLabel={displayAboLabel}
        handleProductAboLabelClick={handleProductAboLabelClick}
      />
      <ProductLabelRedPoint redPointsData={redPointsData} />
      <ProductLabelPromotion {...productLabelPromotionProps} />

      {!isEmpty(trackingUrls) && (
        <ProductLabelCommon label={i18n._('general.label.sponsored')} />
      )}

      <NowLabelWithZipInteractionContainer
        displayNowWidget={displayNowWidget}
        upid={upid}
      />

      <Experiment name={TOP_DEALS_LABEL}>
        {({ variant }) =>
          shouldHaveTopDealLabel ? (
            <li className="u-vertical-align--top u-margin-small--bottom u-margin-small--right o-ProductLabels__ProductLabelContainer">
              <Flag
                label="Top Deal"
                {...(variant === EXPERIMENT_VARIATION.V1
                  ? { topDealV1: true }
                  : { topDealV2: true })}
              />
            </li>
          ) : null
        }
      </Experiment>
      <ProductLabelOutOfTrade isOutOfTrade={isProductOutOfTrade(stockStatus)} />
    </ul>
  )
}

ProductLabels.propTypes = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  displayAboLabel: bool,
  displayNowWidget: bool,
  handleProductAboLabelClick: func,
  i18n: i18nType.isRequired,
  labels: arrayOf(string),
  noLinksForPromotion: bool,
  pharmaObl: bool,
  prescription: bool,
  promotion: string,
  promotionProduct: PromotionProductTypeV2,
  redPointsData: redPointsType,
  stockStatus: shape({}),
  offerTypeSae: bool,
  trackingUrls,
  upid: string,
  id: string,
}

ProductLabels.defaultProps = {
  displayAboLabel: false,
  displayNowWidget: false,
  handleProductAboLabelClick: null,
  pharmaObl: false,
  prescription: false,
  promotion: null,
  promotionProduct: null,
  labels: null,
  noLinksForPromotion: false,
  offerTypeSae: true,
  redPointsData: null,
  stockStatus: {},
  trackingUrls: null,
  upid: null,
  id: null,
}

export default compose(withShopAndRuntimeConfig, withI18n())(ProductLabels)
